﻿import React, { useState, useEffect } from 'react';
import { NavLink } from "react-router-dom";
import { HelpDocument } from '../models/help-document';
import { CrudFunctions } from '../utilities/crud-functions';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

import DataGrid, {
    Column,
    FilterRow,
    Editing,
    Selection,
    Button as ColumnButton,
    HeaderFilter,
    Paging,
    Form,
    Pager,
    Popup,
    Scrolling,
    SearchPanel,
    Toolbar,
    Item as ToolbarItem,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Button } from 'devextreme-react/button';
import { Item } from 'devextreme-react/form';

export default function DocumentationLauncher() {
    const [documentation, setDocumentation] =
        useState<HelpDocument[]>();

    const [selectedHelpDocument, setSelectedHelpDocument] =
        useState<HelpDocument>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, []);

    const gridWidth = 'calc(90vw - 13em)';

    return (
        <div className="pw-form-container">
            <div className="glassware-container">
                <div className="lq-title-bar pw-form-header">
                    <div className="lq-main-title">Documentation</div>

                    <div className="title-bar-spacer" />
                    <div className="save-toolbar">
                        <Button id="goBackButton" text="" icon="arrowleft" type="normal"
                            hint="go back"
                            height={'2.6em'}
                            onClick={goBack} />
                    </div>
                </div>

                <div className="pw-margin-bottom-05">
                    <DataGrid
                        height="40rem"
                        width={gridWidth}
                        dataSource={documentation ? documentation : []}
                        wordWrapEnabled={true}
                        allowColumnResizing={true}
                        columnResizingMode={'widget'}
                        onSelectionChanged={documentationSelectionChanged}
                        onRowValidating={onDocumentationRowValidating}
                        onRowRemoving={onDocumentationRemoving}
                        showBorders={true}>

                        <LoadPanel enabled={true} />
                        <Paging defaultPageSize={30} />
                        <Pager
                            showPageSizeSelector={true}
                            allowedPageSizes={[30, 60, 100]}
                            showInfo={true} />
                        <Selection mode={'single'} />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />

                        <SearchPanel visible={true} />
                        <Scrolling columnRenderingMode={'virtual'} />

                        <Column type="buttons" width="8rem">
                            <ColumnButton hint="audit information" icon="clock"
                                onClick={onDocumentAuditButtonClicked} />
                            <ColumnButton hint="information" icon="info"
                                onClick={onDocumentInformationButtonClicked} />
                            <ColumnButton name="delete" />
                            <ColumnButton render={gotoDocumentationEditorCellRender} />
                            <ColumnButton render={gotoDocumentationViewerCellRender} />
                        </Column>

                        <Column dataField={'title'} width={'15rem'} />
                        <Column dataField={'description'} />
                        <Column dataField={'formName'} width={'15rem'} allowEditing={false} />
                        <Column dataField={'mainDocument'} caption="Main"
                            dataType="boolean" width={'6rem'} allowEditing={false} />
                        <Column dataField={'kbSize'} dataType="number" width={'6rem'} allowEditing={false} />

                        <Toolbar>
                            <ToolbarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Documentation</div>
                                </div>
                            </ToolbarItem>
                        </Toolbar>

                        <Editing
                            mode="popup"
                            useIcons={true}
                            allowAdding={true}
                            allowUpdating={false}
                            allowDeleting={true} >

                            <Popup title="Document" showTitle={true} width="35rem" height="18rem" />
                            <Form>
                                <Item dataField="title" colSpan={2} />
                                <Item dataField="description" colSpan={2} />
                            </Form>

                        </Editing>

                    </DataGrid>
                </div>
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    //change

    function documentationSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            const selectedHelpDocument = e.selectedRowsData[0];
            setSelectedHelpDocument(selectedHelpDocument);
        }
    }

    // validating

    async function onDocumentationRowValidating(e: any) {
        if (e.oldData) {
            let helpDocument = e.oldData as HelpDocument;

            if (helpDocument) {
                let updatedHelpDocument = { ...helpDocument, ...e.newData };
                updateDocumentation(updatedHelpDocument)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Document");
                    });
            }
        }
        else {
            if (e.newData) {
                let helpDocument = e.newData as HelpDocument;

                if (helpDocument) {
                    await insertDocumentation(helpDocument)
                        .then(data => {
                            getDocumentation();
                        })

                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Inserting Document");
                        });
                }
            }
        }
    }

    // delete

    function onDocumentDeleteClick(e: any) {

    }

    function onDocumentationRemoving(e: any) {
        if (selectedHelpDocument && selectedHelpDocument.documentId)
            deleteDocumentation(selectedHelpDocument.documentId)

                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Document");
                });
    }

    // get functions

    async function getDocumentation() {
        await CrudFunctions.getData("documentation/documentationlist")
            .then(data => {
                setDocumentation(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Documentation");
            });
    }

    //POST functions

    async function insertDocumentation(helpDocument: HelpDocument) {
        return CrudFunctions.postData("documentation/helpdocument", helpDocument);
    }

    //PUT functions

    async function updateDocumentation(helpDocument: HelpDocument) {
        return CrudFunctions.putData("documentation/helpdocument", helpDocument);
    }

    // DELETE functions

    async function deleteDocumentation(documentId: number) {
        return CrudFunctions.deleteData(`documentation/helpdocument/${documentId}`);
    }

    //render

    function gotoDocumentationEditorCellRender(e: any) {
        let helpDocument = e.data as HelpDocument;
        let path = `/documentationeditor/${helpDocument.documentId}`;

        if (helpDocument) {
            return <NavLink to={path} className="datagrid-icon-link">
                <span className="dx-icon-edit datagrid-icon de-default-icon-color"></span>
            </NavLink>;
        }
    }

    function gotoDocumentationViewerCellRender(e: any) {
        let helpDocument = e.data as HelpDocument;
        let path = `/documentation/${helpDocument.documentId}`;

        if (helpDocument) {
            return <NavLink to={path} target="_blank" className="datagrid-icon-link">
                <span className="pw-margin-left-05 dx-icon-photo datagrid-icon de-default-icon-color"></span>
            </NavLink>;
        }
    }

    //navigation

    function goBack() {
        history.back();
    }

    //other

    function onDocumentAuditButtonClicked(e: any) {
        if (e.row && e.row.data) {
            /*
            this.setState({
                auditEntity: Entities.Compositions,
                auditRowPointer: e.row.data.compositionId,
                auditInformationShowChildData: false,
                auditInformationDialogVisible: true
            });
            */
        }
    }

    function onDocumentInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            /*
            let itemBasicInitInformation: ItemBasicInformation = {
                rowPointer: e.row.data.compositionId,
                createTimestamp: e.row.data.createTimestamp,
                lastModifiedTimestamp: e.row.data.modifyTimestamp,
                createUser: e.row.data.createUser,
                modifyUser: e.row.data.modifyUser
            };

            this.setState({
                itemBasicInitInformation: itemBasicInitInformation,
                itemBasicInformationDialogVisible: true
            });
            */
        }
    }

    async function getAllData() {
        await getDocumentation();
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
